import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import {
  POST_EDITOR_BLOCK_INDENT_TYPES,
  POST_EDITOR_MAX_INDENT,
  POST_EDITOR_MIN_INDENT,
} from '../lib/post_editor_constants.js';
import { normalizeIndentedBlockquotesInRange } from '../lib/post_block_indent.js';
import { normalizeIndentedListItemsInRange } from '../lib/post_list_indent.js';
import { isResourceRowNode } from '../lib/resource_row_paragraph.js';

// Indent behavior (Google Docs–style):
// - paragraphs / headings / blockquote / resourceTable / horizontalRule: whole-block visual indent
// - lists (bulletList / orderedList):
//     * If the current item (or selected items) CAN be nested → sinkListItem / liftListItem
//     * Otherwise (e.g. first item of the list) → visually indent the whole list block

const NESTED_INDENT_SKIP_TYPES = new Set(['paragraph', 'heading']);

function isInsideIndentContainer($from) {
  for (let d = $from.depth; d > 0; d--) {
    const name = $from.node(d).type.name;
    if (name === 'blockquote' || name === 'resourceTable' || isResourceRowNode($from.node(d))) {
      return true;
    }
  }
  return false;
}

function findIndentTarget(state, preferTypes = []) {
  const { $from } = state.selection;

  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (preferTypes.includes(node.type.name)) {
      return { pos: $from.before(d), node };
    }
  }

  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    const name = node.type.name;
    if (!POST_EDITOR_BLOCK_INDENT_TYPES.includes(name)) continue;
    // Paragraphs inside blockquote / resource rows indent with their container, not alone.
    if (NESTED_INDENT_SKIP_TYPES.has(name) && isInsideIndentContainer($from)) continue;
    return { pos: $from.before(d), node };
  }
  return null;
}

// True when the selection is inside a nested list item (sub-point).
function isInsideSubPoint(state) {
  const { $from } = state.selection;
  let listItemAncestors = 0;
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === 'listItem') listItemAncestors += 1;
  }
  return listItemAncestors > 1;
}

function isIndentableParagraph($pos) {
  for (let d = $pos.depth; d > 0; d--) {
    const name = $pos.node(d).type.name;
    if (name === 'blockquote') return false;
    if (isResourceRowNode($pos.node(d))) return false;
    if (name === 'listItem') return false;
    if (name === 'resourceTable') return false;
  }
  return $pos.parent.type.name === 'paragraph';
}

/** All indentable blocks touched by the current selection (or one block when collapsed). */
function collectIndentTargets(state) {
  const { selection } = state;
  const { from, to, empty } = selection;

  if (empty) {
    const hit = findIndentTarget(state);
    return hit ? [hit] : [];
  }

  const targets = [];
  const seen = new Set();

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === 'paragraph') {
      const $inside = state.doc.resolve(pos + 1);
      if (!isIndentableParagraph($inside)) return;
      if (seen.has(pos)) return;
      seen.add(pos);
      targets.push({ pos, node });
      return false;
    }

    if (
      node.type.name === 'bulletList' ||
      node.type.name === 'orderedList' ||
      node.type.name === 'resourceTable' ||
      node.type.name === 'horizontalRule' ||
      node.type.name === 'blockquote' ||
      node.type.name === 'image' ||
      node.type.name === 'video' ||
      node.type.name === 'embedVideo' ||
      node.type.name === 'postImageCarousel'
    ) {
      if (seen.has(pos)) return;
      seen.add(pos);
      targets.push({ pos, node });
      return false;
    }

    return undefined;
  });

  return targets;
}

function applyIndentDelta(state, dispatch, targets, delta) {
  if (!targets.length) return false;

  const tr = state.tr;
  let modified = false;

  [...targets]
    .sort((a, b) => b.pos - a.pos)
    .forEach(({ pos, node }) => {
      const cur = node.attrs.indent || 0;
      const nextLevel =
        delta > 0
          ? Math.min(POST_EDITOR_MAX_INDENT, cur + 1)
          : Math.max(POST_EDITOR_MIN_INDENT, cur - 1);
      if (nextLevel === cur) return;
      tr.setNodeMarkup(pos, null, {
        ...node.attrs,
        indent: nextLevel <= 0 ? 0 : nextLevel,
      });
      modified = true;
    });

  if (!modified) return false;
  if (dispatch) dispatch(tr);
  return true;
}

export const PostIndentExtension = Extension.create({
  name: 'postIndent',

  priority: 10000,

  addGlobalAttributes() {
    return [
      {
        types: [
          'paragraph',
          'heading',
          'bulletList',
          'orderedList',
          'resourceTable',
          'horizontalRule',
          'blockquote',
          'image',
          'video',
          'embedVideo',
          'postImageCarousel',
        ],
        attributes: {
          indent: {
            default: POST_EDITOR_MIN_INDENT,
            parseHTML: (element) => {
              const raw =
                element.getAttribute('data-ps-indent') ||
                element.getAttribute('data-indent');
              const n = parseInt(raw || '0', 10);
              if (Number.isNaN(n) || n < POST_EDITOR_MIN_INDENT) return POST_EDITOR_MIN_INDENT;
              if (n > POST_EDITOR_MAX_INDENT) return POST_EDITOR_MAX_INDENT;
              return n;
            },
            renderHTML: (attributes) => {
              const level = attributes.indent || 0;
              if (!level || level <= 0) return {};
              return {
                'data-ps-indent': String(level),
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indentBlock:
        () =>
        ({ editor, state, dispatch }) => {
          if (editor?.isActive?.('listItem')) {
            // Try to nest the current item (or selected items) as sub-points (Google Docs-style).
            if (editor.can().sinkListItem('listItem')) {
              return editor.chain().focus().sinkListItem('listItem').run();
            }
            // First item / can't sink → indent the whole list block visually.
            const hit = findIndentTarget(state, ['bulletList', 'orderedList']);
            if (!hit) return false;
            const cur = hit.node.attrs.indent || 0;
            if (cur >= POST_EDITOR_MAX_INDENT) return false;
            return applyIndentDelta(state, dispatch, [hit], 1);
          }

          // Indent the whole blockquote / resource table when the cursor is inside them.
          if (editor?.isActive?.('resourceTable')) {
            const hit = findIndentTarget(state, ['resourceTable']);
            if (hit) return applyIndentDelta(state, dispatch, [hit], 1);
          }
          if (editor?.isActive?.('blockquote')) {
            const hit = findIndentTarget(state, ['blockquote']);
            if (hit) return applyIndentDelta(state, dispatch, [hit], 1);
          }

          const targets = collectIndentTargets(state);
          if (targets.length > 0) {
            return applyIndentDelta(state, dispatch, targets, 1);
          }

          const hit = findIndentTarget(state);
          if (!hit) return false;
          return applyIndentDelta(state, dispatch, [hit], 1);
        },
      outdentBlock:
        () =>
        ({ editor, state, dispatch }) => {
          if (editor?.isActive?.('listItem')) {
            // Sub-points: un-nest within the list.
            if (isInsideSubPoint(state)) {
              if (editor.can().liftListItem('listItem')) {
                return editor.chain().focus().liftListItem('listItem').run();
              }
              return false;
            }

            // Top-level item: if the list has a visual indent, reduce it first
            // (otherwise we'd lift the item out of the list, which the user doesn't want).
            const hit = findIndentTarget(state, ['bulletList', 'orderedList']);
            const cur = hit?.node?.attrs?.indent || 0;
            if (hit && cur > POST_EDITOR_MIN_INDENT) {
              return applyIndentDelta(state, dispatch, [hit], -1);
            }

            // Top-level item, list has no indent → lift out of the list (to paragraph).
            if (editor.can().liftListItem('listItem')) {
              return editor.chain().focus().liftListItem('listItem').run();
            }
            return false;
          }

          if (editor?.isActive?.('resourceTable')) {
            const hit = findIndentTarget(state, ['resourceTable']);
            if (hit) return applyIndentDelta(state, dispatch, [hit], -1);
          }
          if (editor?.isActive?.('blockquote')) {
            const hit = findIndentTarget(state, ['blockquote']);
            if (hit) return applyIndentDelta(state, dispatch, [hit], -1);
          }

          const targets = collectIndentTargets(state);
          if (targets.length > 0) {
            return applyIndentDelta(state, dispatch, targets, -1);
          }

          const hit = findIndentTarget(state);
          if (!hit) return false;
          return applyIndentDelta(state, dispatch, [hit], -1);
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.indentBlock(),
      'Shift-Tab': () => this.editor.commands.outdentBlock(),
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          const tr = newState.tr;
          const size = tr.doc.content.size;
          const listsFixed = normalizeIndentedListItemsInRange(tr, 0, size);
          const quotesFixed = normalizeIndentedBlockquotesInRange(tr, 0, size);
          if (!listsFixed && !quotesFixed) {
            return null;
          }
          return tr;
        },
      }),
    ];
  },
});
